import * as vp from 'vopay';

import moment from 'moment';
import { useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import useFetchPartnerAccountTransactions from '@/hooks/useApi/useFetchPartnerAccountTransactions';
import PartnerAccountList from './PartnerAccountList';
import TransactionsDialogue from './TransactionsDialogue';
import { DialogueHandlerProps, TransactionDialogueHandlers } from './types';
import TransactionsContext, { TransactionsCtx } from './TransactionsContext';

const PartnerAccountTransactions = (): React.JSX.Element => {
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [areDatesValid, setAreDatesValid] = useState(false);
  const [timeRange, setTimeRange] = useState<[vp.Timestamp, vp.Timestamp] | undefined>();
  const [isSubmittingSearch, setIsSubmittingSearch] = useState(false);
  const [isExportingCSV, setIsExportingCSV] = useState(false);
  const [transactionsSearchData, setTransactionsSearchData] = useState<vp.ParentAccountTransactionsRequest>();
  const history = useHistory();

  const [transactions, setTransactions] = useState<vp.AccountTransactionsMap>({});
  const [accountTransactionsList, fetchAccountTransactions] = useFetchPartnerAccountTransactions({});

  const [hasInvalidEndDate, setHasInvalidEndDate] = useState(false);
  const [shouldShowEndDateError, setShouldShowEndDateError] = useState(false);
  const [showTransactionsSearchSuccessAlert, setShowTransactionsSearchSuccessAlert] = useState(false);
  const [transactionsSuccessMessage, setTransactionsSuccessMessage] = useState<string>('');
  const [showTransactionsSearchErrorAlert, setShowTransactionsSearchErrorAlert] = useState(false);
  const [transactionsSearchStatus, setTransactionsSearchStatus] = useState<boolean | null>(null);
  const [transactionsSearchError, setTransactionsSearchError] = useState<string>('');

  useEffect(() => {
    if (timeRange) {
      const FromDate = moment(timeRange?.[0]).format('YYYY-MM-DD');
      const ToDate = moment(timeRange?.[1]).format('YYYY-MM-DD');

      setStartDate(FromDate);
      setEndDate(ToDate);
    }
  }, [timeRange]);

  useEffect(() => {
    setAreDatesValid(isDateValid(startDate) && isDateValid(endDate));
  }, [startDate, endDate]);

  useEffect(() => {
    if (transactionsSearchStatus === false) {
      setShowTransactionsSearchErrorAlert(true);
      setShowTransactionsSearchSuccessAlert(false);
    }
  }, [transactionsSearchStatus]);

  useEffect(() => {
    // if it is the first load of the page and we have saved transactions, get them from that state
    if (!isSubmittingSearch && !isExportingCSV) {
      return () => {};
    }

    const getTransactions = () => {
      // default values when no search has been done
      const currentDateTime = moment().format('YYYY-MM-DD');
      const timeDifference = moment().subtract(1, 'week').format('YYYY-MM-DD');

      const defaultValues = {
        FromDate: timeDifference,
        ToDate: currentDateTime,
      };

      const accountTransactionRequest = transactionsSearchData
        ? {
            ...transactionsSearchData,
            TransactionType:
              transactionsSearchData.TransactionType === 'All' ? undefined : transactionsSearchData.TransactionType,
            ExportCSV: isExportingCSV ? true : false,
          }
        : defaultValues;

      // add the time manually
      accountTransactionRequest.FromDate = startDate || timeDifference;
      accountTransactionRequest.ToDate = endDate || currentDateTime;

      if (!hasInvalidEndDate) {
        fetchAccountTransactions(accountTransactionRequest);
      } else {
        setShouldShowEndDateError(true);
      }
    };

    return getTransactions();
  }, [isSubmittingSearch, isExportingCSV]);

  useEffect(() => {
    if (startDate > endDate) {
      setHasInvalidEndDate(true);
    } else {
      setHasInvalidEndDate(false);
    }
  }, [endDate]);

  useEffect(() => {
    // if it is the first load of the page and we have saved transactions, get them from that state

    const proccessTransactionsData = () => {
      const { Transactions, CSVFile, Success, ErrorMessage } = accountTransactionsList;
      if (Transactions) {
        const transactionList = Object.values(Transactions);
        setTransactions((prevState: vp.AccountTransactionsMap) => ({
          ...prevState,
          transactionList,
        }));
        setTransactionsSearchStatus(Success);
        setTransactionsSearchError(ErrorMessage);
      }

      if (CSVFile) {
        const downloadLink = document.createElement('a');
        const fileName = 'account_transactions.csv';

        downloadLink.href = CSVFile;
        downloadLink.download = fileName;
        downloadLink.click();
      }

      if (!Success && ErrorMessage !== '') {
        setTransactionsSearchStatus(Success);
        setTransactionsSearchError(ErrorMessage);
      }

      setIsExportingCSV(false);
      setIsSubmittingSearch(false);
    };

    proccessTransactionsData();
  }, [accountTransactionsList]);

  function isDateValid(date: string) {
    if (!date) return true;

    const [, mm, dd] = date.split('-');

    const m = parseInt(mm, 10);
    const d = parseInt(dd, 10);

    const isValidMonth = m > 0 && m <= 12;
    const isValidDay = d > 0 && d <= 31 && dd.length === 2;

    return isValidMonth && isValidDay;
  }

  const handlers = {
    handleDialogue: (handlerProps: DialogueHandlerProps<TransactionDialogueHandlers>) => {
      const { type, state, action = 'default', hasNavigation } = handlerProps;

      const dialogueHandlers: TransactionDialogueHandlers = {
        search: {
          success: {
            default: () => {},
          },
          fail: {
            default: () => {
              setShowTransactionsSearchSuccessAlert(false);
              setShowTransactionsSearchErrorAlert(false);
              setTransactionsSuccessMessage('');
              setTransactionsSearchStatus(null);
            },
          },
        },
      };

      const dialogueState = dialogueHandlers[type][state];
      const handler = dialogueState[action] || dialogueState.default;

      handler();

      if (hasNavigation) history.push('/partner-account-transactions');
    },

    handleDate: (values: [vp.Timestamp, vp.Timestamp] | undefined) => {
      setTimeRange(values);
    },

    onTransactionsSearch: (values: vp.ParentAccountTransactionsRequest) => {
      if (isExportingCSV) {
        setTransactionsSearchData({ ...values, ExportCSV: true });
      } else {
        setTransactionsSearchData(values);
        setIsSubmittingSearch(true);
      }
    },

    exportCSV: (): void => {
      setIsExportingCSV(true);
    },
  };

  const transactionsContext: TransactionsCtx = {
    transactionsSearchStatus,
    transactionsSearchError,
    transactionsSuccessMessage,
    showTransactionsSearchSuccessAlert,
    showTransactionsSearchErrorAlert,
  };

  return (
    <TransactionsContext.Provider value={transactionsContext}>
      <PartnerAccountList
        areDatesValid={areDatesValid}
        transactions={transactions}
        handlers={handlers}
        isExportingCSV={isExportingCSV}
        hasInvalidEndDate={hasInvalidEndDate}
        shouldShowEndDateError={shouldShowEndDateError}
        setShouldShowEndDateError={setShouldShowEndDateError}
      />
      <TransactionsDialogue handleDialogue={handlers.handleDialogue} />
    </TransactionsContext.Provider>
  );
};

export default PartnerAccountTransactions;
